import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../i18n";

// Importaciones paralelas del universo propio (feedback de Carlos, 20 oct 2026): el
// catálogo las trata como competencia y así cuentan hasta que la org decida, una a
// una o en bloque. El backend (app/importaciones.py) mueve las filas entre Mis
// productos y Competidores; aquí solo se lista y se decide.
export type ParallelImport = {
  nregistro: string; motivo: "ip" | "extranjero"; decision: "propio" | "competencia" | null;
  decided_by: string | null; decided_at: string | null;
  nombre: string; active_ingredient: string | null; titular: string | null; ofertante: string | null;
  presentaciones: { cn: string; nombre: string; comercializado: boolean }[];
};

const AMBER = "#d97706";
const th: React.CSSProperties = {
  padding: "0.5rem 8px", textAlign: "left", fontSize: "0.62rem", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: 0.8, color: "var(--clr-text4)", whiteSpace: "nowrap",
};
const td: React.CSSProperties = { padding: "0.5rem 8px", verticalAlign: "top" };
const pill = (active: boolean, color: string): React.CSSProperties => ({
  background: active ? color : "transparent", color: active ? "#fff" : "var(--clr-text2)",
  border: `1px solid ${active ? color : "var(--clr-border2)"}`, borderRadius: 999,
  padding: "3px 10px", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
});

export default function ParallelImportsPanel({ canEdit, onChanged }: { canEdit: boolean; onChanged: () => void }) {
  const t = useT();
  const [items, setItems] = useState<ParallelImport[]>([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get<{ items: ParallelImport[] }>("/portfolio/importaciones")
    .then(r => setItems(r.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (items.length === 0) return null;
  const pending = items.filter(i => i.decision === null).length;

  const decide = async (nregistros: string[], decision: "propio" | "competencia") => {
    if (!nregistros.length) return;
    setBusy(true);
    try {
      await api.put("/portfolio/importaciones", { nregistros, decision });
      setSel(new Set());
      await load();
      onChanged();
      setMsg(t("portfolio.parallelSaved"));
      setTimeout(() => setMsg(""), 3000);
    } catch (e: unknown) {
      setMsg(t("portfolio.errorPrefix") + (e instanceof Error ? e.message : ""));
    } finally { setBusy(false); }
  };
  const toggle = (n: string) => setSel(prev => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });
  const allSelected = sel.size === items.length;

  return (
    <div style={{ background: "var(--clr-surface)", border: `1px solid ${pending ? AMBER + "55" : "var(--clr-border)"}`,
                  borderRadius: 18, padding: "0.85rem 1.1rem", marginBottom: 14, boxShadow: "var(--clr-shadow-sm)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: "0.9rem" }}>{t("portfolio.parallelTitle")}</strong>
        <span style={{ fontSize: "0.78rem", color: pending ? AMBER : "var(--clr-text3)", fontWeight: pending ? 600 : 400 }}>
          {pending === 1 ? t("portfolio.parallelPendingOne")
            : pending > 1 ? t("portfolio.parallelPending", { n: pending })
            : `${items.length}`}
        </span>
        <span style={{ flex: 1 }} />
        {msg && <span style={{ fontSize: "0.76rem", color: "var(--clr-text3)" }}>{msg}</span>}
        <button onClick={() => setOpen(o => !o)} style={pill(false, AMBER)}>
          {open ? t("portfolio.parallelHide") : t("portfolio.parallelReview")}
        </button>
      </div>
      {open && (
        <>
          <p style={{ margin: "8px 0 10px", fontSize: "0.78rem", color: "var(--clr-text3)", lineHeight: 1.45 }}>
            {t("portfolio.parallelIntro")}
          </p>
          {canEdit && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.74rem", color: "var(--clr-text3)" }}>{t("portfolio.parallelSelected", { n: sel.size })}</span>
              <button disabled={busy || !sel.size} onClick={() => decide([...sel], "propio")} style={pill(false, "#0891b2")}>
                {t("portfolio.parallelMarkOwn")}
              </button>
              <button disabled={busy || !sel.size} onClick={() => decide([...sel], "competencia")} style={pill(false, "#f59e0b")}>
                {t("portfolio.parallelMarkCompetitor")}
              </button>
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem", minWidth: 760 }}>
              <thead><tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                {canEdit && (
                  <th style={{ ...th, width: 28 }}>
                    <input type="checkbox" checked={allSelected} aria-label="all"
                      onChange={() => setSel(allSelected ? new Set() : new Set(items.map(i => i.nregistro)))} />
                  </th>
                )}
                <th style={th}>{t("portfolio.colProduct")}</th>
                <th style={th}>{t("portfolio.parallelColRegistro")}</th>
                <th style={th}>{t("portfolio.parallelColTitular")}</th>
                <th style={th}>{t("portfolio.parallelColOfertante")}</th>
                <th style={th}>{t("portfolio.parallelColDecision")}</th>
              </tr></thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.nregistro} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    {canEdit && (
                      <td style={td}><input type="checkbox" checked={sel.has(i.nregistro)} onChange={() => toggle(i.nregistro)}
                        aria-label={i.nregistro} /></td>
                    )}
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "var(--clr-text)" }}>{i.nombre}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)" }}>
                        CN {i.presentaciones.map(p => p.cn).join(", ")}
                      </div>
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{i.nregistro}</div>
                      <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)" }}>
                        {i.motivo === "ip" ? t("portfolio.parallelMotivoIp") : t("portfolio.parallelMotivoForeign")}
                      </div>
                    </td>
                    <td style={{ ...td, color: "var(--clr-text3)" }}>{i.titular || "–"}</td>
                    <td style={{ ...td, color: "var(--clr-text2)", fontWeight: 600 }}>{i.ofertante || "–"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {canEdit ? (
                        <>
                          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <button disabled={busy} onClick={() => decide([i.nregistro], "propio")}
                              style={pill(i.decision === "propio", "#0891b2")}>{t("portfolio.parallelOwn")}</button>
                            <button disabled={busy} onClick={() => decide([i.nregistro], "competencia")}
                              style={pill(i.decision === "competencia", "#f59e0b")}>{t("portfolio.parallelCompetitor")}</button>
                          </span>
                          {i.decision === null && (
                            <div style={{ fontSize: "0.68rem", color: AMBER, fontWeight: 600, marginTop: 4 }}>{t("portfolio.parallelPendingLabel")}</div>
                          )}
                        </>
                      ) : (
                        <span style={{ fontSize: "0.74rem" }}>
                          {i.decision === "propio" ? t("portfolio.parallelOwn")
                            : i.decision === "competencia" ? t("portfolio.parallelCompetitor")
                            : t("portfolio.parallelPendingLabel")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
